import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  WF_TRIGGER_TYPE_OPTIONS,
  WF_TIME_PERIOD_REF_OPTIONS,
  WF_RETRY_LIMIT_REF_OPTIONS,
  WF_RETRY_INTERVAL_REF_OPTIONS,
  WF_RECORD_TYPES_BY_MODULE,
  getWorkflowModuleLabel,
  getRecordTypeLabel,
  getStatusLabel,
  labelFromOptions,
  labelTimePeriodRef,
  labelRetryLimitRef,
  labelRetryIntervalRef,
  labelMovementTiming,
  findDuplicateWorkflowRule,
  findOverlappingWorkflowRule,
  findConflictingWorkflowRule,
  findWorkflowStatusLoop,
  getStatusesForRecordType,
  getTriggerConditions,
  getRetryLimitNumericValue,
  getTimePeriodRefOption,
  getRetryLimitRefOption,
  getRetryIntervalRefOption,
  normalizeWorkflowForm,
  normalizeWorkflowRuleScope,
  normalizeWorkflowStatus,
  normalizeWorkflowTriggerType,
  nextWorkflowRuleId,
  formatWorkflowConditionsDisplay,
} from './workflowConfig';
import { emitConfigurationHistoryBatch } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  accountId: 'ADM-0002',
};

const TRACKED_FIELDS = [
  'ruleName',
  'description',
  'module',
  'recordType',
  'currentStatus',
  'nextStatus',
  'triggerType',
  'triggerEvent',
  'timeStartsFrom',
  'timePeriodRef',
  'cancellationEvents',
  'retryLimitRef',
  'retryIntervalRef',
  'conditions',
];

export function workflowActor() {
  return { ...ACTOR };
}

export function canAccessWorkflowConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewWorkflowConfiguration(auth) {
  return canAccessWorkflowConfiguration(auth);
}

export function canAddWorkflowRule(auth) {
  return canAccessWorkflowConfiguration(auth);
}

export function canEditWorkflowRule(auth) {
  return canAccessWorkflowConfiguration(auth);
}

export function canSaveWorkflowRule(auth) {
  return canAccessWorkflowConfiguration(auth);
}

function addError(result, key, message) {
  if (!result.errors[key]) result.errors[key] = message;
  if (!result.summary.includes(message)) result.summary.push(message);
}

function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

function isBlank(value) {
  return value == null || !String(value).trim();
}

export function ruleSnapshotFromForm(formData) {
  const normalized = normalizeWorkflowForm(formData || {});
  const isTime = normalized.triggerType === 'time_based';
  return {
    ruleName: normalized.ruleName?.trim() || '',
    description: normalized.description?.trim() || '',
    module: normalized.module,
    recordType: normalized.recordType,
    currentStatus: normalized.currentStatus,
    nextStatus: normalized.nextStatus,
    triggerType: normalized.triggerType,
    triggerEvent: isTime ? '' : normalized.triggerEvent,
    triggerCondition: isTime ? normalized.timeStartsFrom : normalized.triggerEvent,
    timeStartsFrom: isTime ? normalized.timeStartsFrom : '',
    timePeriodRef: isTime ? normalized.timePeriodRef : '',
    movementTiming: '',
    cancellationEvents: normalized.cancellationEvents || [],
    retryLimitRef: normalized.retryLimitRef,
    retryIntervalRef: normalized.retryIntervalRef,
    conditions: Array.isArray(normalized.conditions) ? normalized.conditions : [],
    changeReason: normalized.changeReason?.trim() || '',
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeWorkflowForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeWorkflowForm({ ...rule, pendingDraft: null });
}

function fieldLabel(key) {
  const labels = {
    ruleName: 'Rule Name',
    description: 'Internal Description',
    module: 'Module',
    recordType: 'Record Type',
    currentStatus: 'Current Status',
    nextStatus: 'Next Status',
    triggerType: 'Trigger Type',
    triggerEvent: 'Trigger Event',
    timeStartsFrom: 'Time Starts From',
    timePeriodRef: 'Time Period',
    cancellationEvents: 'Cancellation Events',
    retryLimitRef: 'Retry Limit',
    retryIntervalRef: 'Retry Interval',
    conditions: 'Conditions',
    changeReason: 'Change Reason',
  };
  return labels[key] || key;
}

function displayFieldValue(key, value, context = {}) {
  if (key === 'conditions') return formatWorkflowConditionsDisplay({ conditions: value });
  if (key === 'cancellationEvents') {
    const events = Array.isArray(value) ? value.filter(Boolean) : [];
    return events.length ? events.join(', ') : 'None selected';
  }
  if (value == null || value === '') return '—';
  if (key === 'module') return getWorkflowModuleLabel(value);
  if (key === 'recordType') return getRecordTypeLabel(context.module, value);
  if (key === 'currentStatus' || key === 'nextStatus') {
    return getStatusLabel(context.recordType, value);
  }
  if (key === 'triggerType') {
    return labelFromOptions(WF_TRIGGER_TYPE_OPTIONS, normalizeWorkflowTriggerType(value));
  }
  if (key === 'timePeriodRef') return labelTimePeriodRef(value);
  if (key === 'retryLimitRef') return labelRetryLimitRef(value);
  if (key === 'retryIntervalRef') return labelRetryIntervalRef(value);
  if (key === 'movementTiming') return labelMovementTiming(value);
  return String(value);
}

export function diffRuleSnapshots(prev, next) {
  const changes = [];
  TRACKED_FIELDS.forEach((key) => {
    const oldDisplay = displayFieldValue(key, prev?.[key], prev || {});
    const newDisplay = displayFieldValue(key, next?.[key], next || {});
    if (oldDisplay !== newDisplay) {
      changes.push({ field: fieldLabel(key), previousValue: oldDisplay, newValue: newDisplay });
    }
  });
  return changes;
}

export function appendConfigurationHistory(rule, action, changes, status, version, changeReason = '') {
  const ts = new Date().toISOString();
  const actor = workflowActor();
  const base = Array.isArray(rule.configurationHistory) ? rule.configurationHistory : [];
  const changeRows = changes.length ? changes : [{ field: '—', previousValue: '—', newValue: '—' }];
  const entries = changeRows.map((c) => ({
    ruleId: rule.id,
    version,
    action,
    field: c.field,
    previousValue: c.previousValue,
    newValue: c.newValue,
    status,
    actor: actor.name,
    role: actor.role,
    accountId: actor.accountId,
    timestamp: ts,
    changeReason,
  }));
  try {
    emitConfigurationHistoryBatch({
      rows: entries,
      module: 'Configuration — Workflow',
      sourceModuleRef: 'configuration_workflow',
      changedItem: rule.ruleName || rule.id,
      itemId: rule.id,
      actor: { name: actor.name, role: actor.role },
      stamp: ts,
      changeType: action === 'Add' || action === 'Created' ? 'Add' : 'Edit',
      changeReason,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  return [...base, ...entries];
}

export function appendTestHistory(rule, summary, passed) {
  const actor = workflowActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function collectWorkflowSaveErrors(formData, allRules, excludeId, options = {}) {
  const result = { errors: {}, summary: [] };
  const form = normalizeWorkflowForm(formData || {});
  const scope = normalizeWorkflowRuleScope(form.module, form.recordType);
  const mod = scope.module;
  const rt = scope.recordType;
  const isTime = form.triggerType === 'time_based';

  if (options.canSave === false) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }

  if (options.checksAvailable === false) {
    addError(result, 'checksUnavailable', 'Required checks could not be completed. Try again.');
    return result;
  }

  if (isBlank(form.ruleName)) addError(result, 'ruleName', 'Complete the required fields.');
  if (isBlank(form.description)) addError(result, 'description', 'Complete the required fields.');
  if (!mod) addError(result, 'module', 'Complete the required fields.');
  if (!rt) addError(result, 'recordType', 'Complete the required fields.');
  if (!form.currentStatus) addError(result, 'currentStatus', 'Complete the required fields.');
  if (!form.nextStatus) addError(result, 'nextStatus', 'Complete the required fields.');
  if (!form.triggerType) addError(result, 'triggerType', 'Complete the required fields.');
  if (isBlank(form.changeReason)) addError(result, 'changeReason', 'Enter a Change Reason.');

  if (form.ruleName?.trim()) {
    const nameClash = (allRules || []).find(
      (r) => r.id !== excludeId && namesMatch(r.ruleName, form.ruleName),
    );
    if (nameClash) addError(result, 'ruleName', 'This Rule Name is already used.');
  }

  const recordTypes = WF_RECORD_TYPES_BY_MODULE[mod] || [];
  if (mod && rt && !recordTypes.some((o) => o.value === rt)) {
    addError(result, 'recordType', 'Select a compatible Module and Record Type.');
  }

  const statuses = getStatusesForRecordType(rt);
  const cur = normalizeWorkflowStatus(form.currentStatus);
  const next = normalizeWorkflowStatus(form.nextStatus);
  if (cur && statuses.length && !statuses.some((s) => s.value === cur)) {
    addError(result, 'currentStatus', 'Select a supported status movement.');
  }
  if (next && statuses.length && !statuses.some((s) => s.value === next)) {
    addError(result, 'nextStatus', 'Select a supported status movement.');
  }
  if (cur && next && cur === next) {
    addError(result, 'nextStatus', 'Select a supported status movement.');
  }

  const triggerOptions = getTriggerConditions(mod, rt, form.triggerType);
  if (form.triggerType === 'event_based') {
    if (!form.triggerEvent) {
      addError(result, 'triggerEvent', triggerOptions.length ? 'Complete the required fields.' : 'No compatible choices available');
    } else if (triggerOptions.length && !triggerOptions.some((c) => c.value === form.triggerEvent)) {
      addError(result, 'triggerEvent', 'Select a compatible trigger.');
    } else if (!triggerOptions.length) {
      addError(result, 'triggerEvent', 'No compatible choices available');
    }
  }

  if (isTime) {
    if (!form.timeStartsFrom) {
      addError(
        result,
        'timeStartsFrom',
        triggerOptions.length ? 'Complete the required fields.' : 'No compatible choices available',
      );
    } else if (triggerOptions.length && !triggerOptions.some((c) => c.value === form.timeStartsFrom)) {
      addError(result, 'timeStartsFrom', 'Select a compatible trigger.');
    } else if (!triggerOptions.length) {
      addError(result, 'timeStartsFrom', 'No compatible choices available');
    }

    if (!form.timePeriodRef) {
      addError(
        result,
        'timePeriodRef',
        WF_TIME_PERIOD_REF_OPTIONS.length
          ? 'Complete the required fields.'
          : 'This reference is unavailable or incompatible.',
      );
    } else if (!getTimePeriodRefOption(form.timePeriodRef)) {
      addError(result, 'timePeriodRef', 'This reference is unavailable or incompatible.');
    }
  }

  if (!form.retryLimitRef) {
    addError(
      result,
      'retryLimitRef',
      WF_RETRY_LIMIT_REF_OPTIONS.length
        ? 'Complete the required fields.'
        : 'This reference is unavailable or incompatible.',
    );
  } else if (!getRetryLimitRefOption(form.retryLimitRef)) {
    addError(result, 'retryLimitRef', 'This reference is unavailable or incompatible.');
  }

  const retryLimitValue = getRetryLimitNumericValue(form.retryLimitRef);
  if (retryLimitValue != null && retryLimitValue > 0) {
    if (!form.retryIntervalRef) {
      addError(
        result,
        'retryIntervalRef',
        WF_RETRY_INTERVAL_REF_OPTIONS.length
          ? 'Complete the required fields.'
          : 'This reference is unavailable or incompatible.',
      );
    } else if (!getRetryIntervalRefOption(form.retryIntervalRef)) {
      addError(result, 'retryIntervalRef', 'This reference is unavailable or incompatible.');
    }
  }

  const conditions = Array.isArray(form.conditions) ? form.conditions : [];
  conditions.forEach((condition, index) => {
    if (!condition.field || !condition.operator) {
      addError(result, `condition_${index}`, 'Complete or remove this Condition.');
    }
  });

  const candidate = {
    ...form,
    module: mod,
    recordType: rt,
  };

  const dup = findDuplicateWorkflowRule(allRules, candidate, excludeId);
  if (dup) addError(result, 'duplicate', `This Rule duplicates ${dup.id}.`);

  const overlap = findOverlappingWorkflowRule(allRules, candidate, excludeId);
  if (overlap && (!dup || overlap.id !== dup.id)) {
    addError(result, 'overlap', `This Rule overlaps ${overlap.id}.`);
  }

  const conflict = findConflictingWorkflowRule(allRules, candidate, excludeId);
  if (conflict) addError(result, 'conflict', `This Rule conflicts with ${conflict.id}.`);

  const loop = findWorkflowStatusLoop(allRules, candidate, excludeId);
  if (loop) addError(result, 'loop', 'This Rule creates an automatic status loop.');

  if (excludeId && options.originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    const currentUpdated = current?.lastUpdated || current?.modifiedOn;
    if (currentUpdated && currentUpdated !== options.originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule has changed. Review the latest saved Rule.');
    }
  }

  return result;
}

/** @deprecated use collectWorkflowSaveErrors */
export function collectActivationErrors(formData, allRules, excludeId) {
  return collectWorkflowSaveErrors(formData, allRules, excludeId).summary;
}

export function runWorkflowRuleTest(formData, allRules) {
  const lines = [];
  const form = normalizeWorkflowForm(formData || {});
  const scope = normalizeWorkflowRuleScope(form.module, form.recordType);

  lines.push(`Module: ${getWorkflowModuleLabel(scope.module) || '—'}`);
  lines.push(`Record Type: ${getRecordTypeLabel(scope.module, scope.recordType) || '—'}`);
  lines.push(`Current Status: ${getStatusLabel(scope.recordType, form.currentStatus) || '—'}`);
  lines.push(`Next Status: ${getStatusLabel(scope.recordType, form.nextStatus) || '—'}`);
  lines.push(
    `Trigger Type: ${labelFromOptions(WF_TRIGGER_TYPE_OPTIONS, form.triggerType) || '—'}`,
  );
  if (form.triggerType === 'time_based') {
    lines.push(`Time Starts From: ${form.timeStartsFrom || '—'}`);
    lines.push(`Time Period: ${labelTimePeriodRef(form.timePeriodRef)}`);
  } else {
    lines.push(`Trigger Event: ${form.triggerEvent || '—'}`);
  }
  lines.push(`Conditions: ${formatWorkflowConditionsDisplay(form)}`);

  const validation = collectWorkflowSaveErrors(form, allRules, form.id || undefined);
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...validation.summary.map((c) => `• ${c}`)].join('\n'),
    };
  }

  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no records were modified.',
    detail: [...lines, '', 'No duplicate, overlapping or conflicting rules detected.'].join('\n'),
  };
}

function snapshotsEqual(a, b) {
  return diffRuleSnapshots(a, b).length === 0;
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const actor = workflowActor();
  const nowIso = payload.lastUpdated || payload.modifiedOn || new Date().toISOString();

  if (!isNew) {
    const prevSnap = ruleSnapshotFromForm(publishedBaseline);
    if (snapshotsEqual(prevSnap, snapshot)) {
      return {
        rules: allRules,
        record: getPublishedRule(publishedBaseline),
        unchanged: true,
        isNew: false,
      };
    }
  }

  const id = isNew ? payload.id || nextWorkflowRuleId(allRules) : publishedBaseline.id;
  const changes = isNew
    ? diffRuleSnapshots({}, snapshot)
    : diffRuleSnapshots(ruleSnapshotFromForm(publishedBaseline), snapshot);

  const record = {
    ...(isNew ? {} : getPublishedRule(publishedBaseline)),
    ...snapshot,
    id,
    version: isNew ? 1 : (publishedBaseline.version || 1) + (changes.length ? 0 : 0) || 1,
    pendingDraft: null,
    configurationHistory: appendConfigurationHistory(
      { id, ruleName: snapshot.ruleName, configurationHistory: publishedBaseline?.configurationHistory || [] },
      isNew ? 'Add' : 'Edit',
      changes,
      'saved',
      isNew ? 1 : publishedBaseline.version || 1,
      snapshot.changeReason,
    ),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor.name : publishedBaseline.addedBy || publishedBaseline.createdBy || actor.name,
    lastUpdated: nowIso,
    lastUpdatedBy: actor.name,
    createdOn: isNew ? nowIso : publishedBaseline.createdOn || publishedBaseline.dateAdded || nowIso,
    createdBy: isNew ? actor.name : publishedBaseline.createdBy || publishedBaseline.addedBy || actor.name,
    modifiedOn: nowIso,
    modifiedBy: actor.name,
  };

  const rules = isNew
    ? [...allRules.filter((r) => r.id !== record.id), record]
    : allRules.map((r) => (r.id === record.id ? record : r));

  return { rules, record, unchanged: false, isNew };
}

export function saveWorkflowRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectWorkflowSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated: options.originalLastUpdated
      ?? publishedBaseline?.lastUpdated
      ?? publishedBaseline?.modifiedOn,
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { form: 'The Rule could not be saved. Your changes have been kept for review.' },
      summary: ['The Rule could not be saved. Your changes have been kept for review.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

/** Kept for configuration test simulation registry compatibility. */
export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const result = saveWorkflowRule(allRules, {
    ...payload,
    changeReason: payload.changeReason || 'Save from configuration test',
  }, publishedBaseline || {}, { canSave: true, checksAvailable: true });
  return result.rules;
}

/** @deprecated draft path removed — Rules save directly. */
export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline) {
  return mergeRulesAfterActivate(allRules, payload, publishedBaseline);
}

/** @deprecated no Rule State / inactivation. */
export function mergeRulesAfterInactivate(allRules) {
  return allRules;
}

/** @deprecated no Rule State / list activate. */
export function mergeRulesAfterListActivate(allRules) {
  return allRules;
}
